import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {
  advantages,
  gallery,
  processSteps,
  services,
  solutions,
  testimonials,
  trustItems
} from '../config/siteData';

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.16 },
  transition: { duration: 0.6 }
};

export function SectionHeading({ eyebrow, title, copy, centered = false }) {
  return (
    <motion.div className={`section-heading ${centered ? 'centered' : ''}`} {...reveal}>
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h2>{title}</h2>
      {copy && <p>{copy}</p>}
    </motion.div>
  );
}

export function Hero() {
  return (
    <section className="hero" id="home">
      <div className="hero-content">
        <motion.div className="hero-copy" {...reveal}>
          <span className="eyebrow">Austin's Turnaround Experts</span>
          <h1>Your Property.<br /><em>Guest-Ready.</em> Every Time.</h1>
          <p>Professional short-term rental turnovers for Austin-area hosts who don't have time to worry about what's happening between checkout and check-in.</p>
          <div className="hero-actions">
            <a className="button" href="#quote">Request a Turnover Quote</a>
            <a className="text-link" href="#services">See Our Services <SafeIcon icon={FiIcons.FiArrowUpRight} /></a>
          </div>
          <span className="trust-note"><SafeIcon icon={FiIcons.FiMapPin} /> Serving Austin &amp; surrounding areas</span>
        </motion.div>
        <motion.div className="hero-image" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }}>
          <img src="https://images.unsplash.com/photo-1615874694520-474822394e73?auto=format&fit=crop&w=1600&q=90" alt="Bright Austin short-term rental bedroom prepared for guests" />
          <div className="image-badge">
            <SafeIcon icon={FiIcons.FiCheck} />
            <span><strong>Turnaround complete</strong><small>Ready before check-in</small></span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function TrustStrip() {
  return (
    <section className="trust-strip" aria-label="Service benefits">
      <div className="trust-grid">
        {trustItems.map((item) => (
          <div className="trust-item" key={item.label}>
            <SafeIcon icon={item.icon} />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ProblemSolution() {
  return (
    <>
      <section className="problem section">
        <motion.div className="problem-grid" {...reveal}>
          <div>
            <img
              src="https://media-manager-c.questera.ai/greta-media/f59708bb9af3ffea40f962a8d12d285c2d07055c5905b57e8f038b23faf998248513260fbde40d878428d8efe8733758/images/aW1hZ2UvanBlZw==/ca5244ce736471ceb1102baf621efffa.jpg"
              alt="The Cleaning Bees team preparing a property in Austin"
              style={{
                display: 'block',
                width: '100%',
                height: '270px',
                marginBottom: '38px',
                objectFit: 'cover',
                objectPosition: 'center'
              }}
            />
            <span className="eyebrow">The five-hour window</span>
            <h2>Your guests don't see the work. <em>They see the result.</em></h2>
          </div>
          <div className="problem-copy">
            <p>Between an 11:00 AM checkout and a 4:00 PM check-in, there's a lot that has to go right. Linens need to be handled. Supplies need to be restocked. Technology needs to work. The property needs to look perfect.</p>
            <strong>That's where The Cleaning Bees comes in.</strong>
          </div>
        </motion.div>
      </section>
      <section className="solution section">
        <SectionHeading eyebrow="More than a clean" title="Your Turnaround, Completely Handled." copy="We handle the details that keep your short-term rental running smoothly—from cleaning and off-site laundry to inventory, restocking and light property maintenance." />
        <div className="solution-grid">
          {solutions.map(([title, copy, icon], index) => (
            <motion.article className="solution-card" key={title} {...reveal} transition={{ duration: 0.5, delay: index * 0.06 }}>
              <SafeIcon icon={icon} />
              <span>0{index + 1}</span>
              <h3>{title}</h3>
              <p>{copy}</p>
            </motion.article>
          ))}
        </div>
      </section>
    </>
  );
}

export function Services() {
  return (
    <section className="services section" id="services">
      <SectionHeading eyebrow="Complete turnover support" title="Everything Your Turnover Needs." copy="One reliable team handling the details between every stay." centered />
      <div className="services-grid">
        {services.map(([title, copy, icon], index) => (
          <motion.article className="service-card" key={title} {...reveal}>
            <div className="service-top">
              <SafeIcon icon={icon} />
              <span>{String(index + 1).padStart(2, '0')}</span>
            </div>
            <h3>{title}</h3>
            <p>{copy}</p>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

export function Timeline() {
  const steps = ['11:00 AM · Checkout', 'Property Inspection', 'Clean + Reset', 'Laundry + Restock', 'Final Check', '4:00 PM · Guest Check-In'];

  return (
    <section className="timeline-section section">
      <SectionHeading eyebrow="Between checkout & check-in" title="Four Hours. One Goal." copy="We're built around the tight window between checkout and check-in." centered />
      <motion.div className="timeline" {...reveal}>
        {steps.map((step, index) => (
          <React.Fragment key={step}>
            <div className={`timeline-step ${index === 0 || index === steps.length - 1 ? 'highlight' : ''}`}>
              <span>{index + 1}</span>
              <strong>{step}</strong>
            </div>
            {index < steps.length - 1 && <div className="timeline-line" />}
          </React.Fragment>
        ))}
      </motion.div>
    </section>
  );
}

export function Process() {
  return (
    <section className="process section" id="process">
      <SectionHeading eyebrow="How it works" title="Simple for You. Thorough for Us." centered />
      <div className="process-grid">
        {processSteps.map(([title, copy], index) => (
          <motion.article className="process-step" key={title} {...reveal}>
            <span>{String(index + 1).padStart(2, '0')}</span>
            <div>
              <h3>{title}</h3>
              <p>{copy}</p>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

export function About() {
  return (
    <section className="about section" id="about">
      <motion.div className="about-image" {...reveal}>
        <img src="https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1400&q=90" alt="Premium Austin vacation rental interior" />
        <div className="about-stat"><strong>One team.</strong><span>Every detail handled.</span></div>
      </motion.div>
      <motion.div className="about-copy" {...reveal}>
        <span className="eyebrow">About The Cleaning Bees</span>
        <h2>Built for Hosts Who Expect More.</h2>
        <p><strong>At The Cleaning Bees, we are your dedicated Turnaround Experts.</strong></p>
        <p>Maintaining Superhost status requires more than someone showing up with cleaning supplies. It requires consistency, communication and attention to the details guests notice.</p>
        <p>We provide Austin-area short-term rental owners with a hands-off experience by managing everything from cleaning and off-site laundry to supply restocking and light property maintenance.</p>
        <p>Our priority is simple: keep your property pristine, fully stocked and ready for five-star guest experiences.</p>
        <p className="gold-copy">Let us handle the turnaround, so you can focus on your guests.</p>
      </motion.div>
    </section>
  );
}

export function WhyUs() {
  return (
    <section className="why section">
      <SectionHeading eyebrow="Why Cleaning Bees" title="More Than a Cleaning Crew." centered />
      <div className="why-grid">
        {advantages.map(([title, copy, icon]) => (
          <motion.article className="why-card" key={title} {...reveal}>
            <SafeIcon icon={icon} />
            <div><h3>{title}</h3><p>{copy}</p></div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

export function OwnerExperience() {
  return (
    <section className="owner">
      <div className="owner-image" role="img" aria-label="Relaxed property owner in a modern home" />
      <motion.div className="owner-copy" {...reveal}>
        <span className="eyebrow">The owner experience</span>
        <h2>Stop Managing Turnovers. Start Managing Your Property.</h2>
        <p>You shouldn't have to coordinate cleaners, laundry, supplies, batteries, guest amenities and last-minute turnover details every time someone checks out.</p>
        <p>With The Cleaning Bees, you have one team responsible for getting your property ready for the next guest.</p>
        <a className="button" href="#quote">Let's Talk About Your Property</a>
      </motion.div>
    </section>
  );
}

export function Gallery() {
  return (
    <section className="gallery-section section">
      <SectionHeading eyebrow="The work behind every stay" title="Hospitality, Down to the Detail." copy="Built to showcase guest-ready properties, our team and everything behind the turnaround." />
      <div className="gallery-grid">
        {gallery.map((item, index) => (
          <motion.figure className={`gallery-item gallery-${index + 1}`} key={`${item.category}-${index}`} {...reveal}>
            <img src={item.image} alt={item.alt} loading="lazy" />
            <figcaption>{item.category}</figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}

export function Testimonials() {
  return (
    <section className="testimonials section">
      <SectionHeading eyebrow="Client stories" title="The Standard Hosts Remember." centered />
      <div className="testimonial-grid">
        {testimonials.map((item, index) => (
          <motion.blockquote className="testimonial" key={item.quote} {...reveal}>
            <span>Placeholder testimonial {String(index + 1).padStart(2, '0')}</span>
            <SafeIcon icon={FiIcons.FiMessageCircle} />
            <p>“{item.quote}”</p>
            <footer>— {item.author}</footer>
          </motion.blockquote>
        ))}
      </div>
      <p className="replacement-note">Sample layout content—replace with verified client testimonials before publishing.</p>
    </section>
  );
}